import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import AiBanner from './AiBanner';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import './effectBlocks.css';

const COLORS = ['#d9363e', '#ffe14d', '#2b7a4b', '#111111', '#ffffff'];

const DEFAULT_PARAMS = {
    x: 0.25,
    y: 0.3,
    w: 0.5,
    h: 0.28,
    color: '#d9363e',
    filled: false,
    thicknessFrac: 0.008,
};

/**
 * BoxBlockEditor — phase-2 effect block: draw a movable, resizable rectangle to
 * highlight (outline) or censor (filled) part of a clip, then compile it into
 * the plan via ApplyBlocks -> drawbox_tool. Mirrors TextBlockEditor.
 *
 * Props: videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, onApplied, onClose
 */
function BoxBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, aiSuggestion, onApplied, onClose }) {
    const [params, setParams] = useState(() => ({ ...DEFAULT_PARAMS, ...(aiSuggestion?.params || {}) }));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const block = useMemo(() => ({ id: 'box-1', type: 'box', source: sourceFileName, params }), [params, sourceFileName]);

    const update = (patch) => setParams((prev) => ({ ...prev, ...patch }));

    const canApply = Boolean(
        videoWorkspaceId && sourceFileName &&
        Number(measured?.width) > 0 && Number(measured?.height) > 0 &&
        (params.w || 0) > 0 && (params.h || 0) > 0
    );

    const handleApply = async () => {
        if (!canApply) {
            setError('Draw a box and make sure the clip has loaded.');
            return;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const outBlock = {
                id: 'box-1',
                type: 'box',
                source: sourceFileName,
                startTime: 0,
                endTime: null, // phase 2: whole clip
                params,
            };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [outBlock], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
            } else {
                setError(res.myJson?.detail || 'Failed to apply the box.');
            }
        } catch (err) {
            setError('Error applying edit: ' + err.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="eb-editor">
            <AiBanner rationale={aiSuggestion?.rationale} />
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="box-1"
                    onChange={(_id, next) => setParams(next)}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Box</h5>
                    <p className="eb-hint">Drag to move · drag a corner to resize.</p>

                    <div className="eb-ctl">
                        <span>Style</span>
                        <div className="eb-seg">
                            <button type="button" aria-pressed={!params.filled} onClick={() => update({ filled: false })}>
                                Highlight
                            </button>
                            <button type="button" aria-pressed={!!params.filled} onClick={() => update({ filled: true })}>
                                Censor
                            </button>
                        </div>
                    </div>

                    {!params.filled && (
                        <label className="eb-ctl">
                            <span>Thickness <em>{Math.round((params.thicknessFrac || 0) * 1000) / 10}%</em></span>
                            <input
                                type="range" min="0.2" max="3" step="0.1"
                                value={(params.thicknessFrac || 0.008) * 100}
                                onChange={(e) => update({ thicknessFrac: Number(e.target.value) / 100 })}
                            />
                        </label>
                    )}

                    <div className="eb-ctl">
                        <span>Color</span>
                        <div className="eb-swatches">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className="eb-sw"
                                    style={{ background: c }}
                                    aria-pressed={params.color === c}
                                    aria-label={`Color ${c}`}
                                    onClick={() => update({ color: c })}
                                />
                            ))}
                        </div>
                    </div>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!canApply || applying} onClick={handleApply}>
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BoxBlockEditor;
